/**
 * Derives team slot availability from API limits payload (teams2/owner or teams2/limits).
 *
 * @param {Object} [limits]
 * @returns {{
 *   totalSlots: number,
 *   usedSlots: number,
 *   availableSlots: number,
 *   sponsorListings: number,
 *   slotsPerSponsor: number,
 *   isAllFreeAccount: boolean,
 *   hasAvailableSlots: boolean,
 *   isSlotsFull: boolean,
 *   gracePeriodActive: boolean,
 *   gracePeriodDaysLeft: number,
 *   gracePeriodExcessMembers: number
 * }}
 */
/** Sent or Accepted invites consume a team slot (matches teams2 used_slots). */
export function memberCountsTowardUsedSlots(member) {
  if (!member) {
    return false;
  }
  const status = Number(member.status);
  return status === 0 || status === 1;
}

/**
 * @param {Object} limits
 * @param {number} delta - e.g. -1 after remove, +1 after invite
 */
export function applySlotDelta(limits = {}, delta = 0) {
  const totalSlots = Number(limits.total_slots) || 0;
  const usedSlots = Math.max(0, (Number(limits.used_slots) || 0) + delta);

  return {
    ...limits,
    used_slots: usedSlots,
    available_slots: Math.max(0, totalSlots - usedSlots)
  };
}

export function getTeamSlotsState(limits = {}) {
  const sponsorListings = Math.max(0, Number(limits.sponsor_listings) || 0);
  const slotsPerSponsor = Math.max(1, Number(limits.slots_per_sponsor) || 3);
  const totalSlots = Math.max(1, Number(limits.total_slots) || (sponsorListings > 0 ? sponsorListings * slotsPerSponsor : 1));
  const usedSlots = Number(limits.used_slots) || 0;
  const availableSlots = limits.available_slots != null ? Math.max(0, Number(limits.available_slots)) : Math.max(0, totalSlots - usedSlots);
  const hasAvailableSlots = availableSlots > 0;
  const isAllFreeAccount = sponsorListings === 0;

  return {
    totalSlots,
    usedSlots,
    availableSlots,
    sponsorListings,
    slotsPerSponsor,
    isAllFreeAccount,
    hasAvailableSlots,
    isSlotsFull: !hasAvailableSlots,
    gracePeriodActive: Boolean(limits.grace_period_active),
    gracePeriodDaysLeft: Number(limits.grace_period_days_left) || 0,
    gracePeriodExcessMembers: Number(limits.grace_period_excess_members) || 0
  };
}

export function getTeamSlotsCalculationDetail(limits = {}) {
  const { sponsorListings, slotsPerSponsor, totalSlots, isAllFreeAccount } = getTeamSlotsState(limits);

  if (isAllFreeAccount) {
    return "All listings are free — 1 team member included";
  }

  const listingLabel = sponsorListings === 1 ? "sponsor listing" : "sponsor listings";
  const slotsLabel = slotsPerSponsor === 1 ? "slot" : "slots";
  return `${sponsorListings} ${listingLabel} × ${slotsPerSponsor} ${slotsLabel} each = ${totalSlots} team members`;
}

export function getTeamSlotsSubtitle(limits = {}) {
  const { totalSlots, isAllFreeAccount, availableSlots, isSlotsFull } = getTeamSlotsState(limits);

  if (isAllFreeAccount) {
    return isSlotsFull ? "Free account limit: 1 team member (no additional slots)" : "Free account: 1 team member included";
  }

  return isSlotsFull
    ? `Account limit reached: ${totalSlots} team members (${availableSlots} available)`
    : `You can add up to ${totalSlots} team members (${availableSlots} available)`;
}

export function getTeamSlotsFullMessage(limits = {}) {
  const slotsState = getTeamSlotsState(limits);

  if (slotsState.gracePeriodActive) {
    return null;
  }

  if (slotsState.isAllFreeAccount) {
    return "No additional slots available on a free account. Sponsor a listing to unlock 3 team members per sponsor listing.";
  }

  return "All team slots are in use. Remove a member or add sponsor listings to invite more.";
}

export function getTeamSlotsAvailableLabel(limits = {}) {
  const { availableSlots, isSlotsFull } = getTeamSlotsState(limits);

  if (isSlotsFull) {
    return "No team slots available";
  }

  if (availableSlots === 1) {
    return "1 team slot available";
  }

  return `${availableSlots} team slots available`;
}

export function shouldShowSponsorBenefits(limits = {}) {
  const { isAllFreeAccount, gracePeriodActive } = getTeamSlotsState(limits);

  return !isAllFreeAccount || gracePeriodActive;
}
